import React, { useState } from "react";
import {
    Button,
    Grid,
    Paper,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import TableCommon from "../../../components/Common/Table";
import { handleDateTimeDefault } from "../../../helpers/utils";
import {
    useGetWalletTransactionListQuery,
} from "../../../store/members/membersApis";

const WalletTransactionsContainer = (props) => {
    const {
        pagination,
        handlePaginationState,
    } = props;

    // ---------------------------------------------------------
    // Search and Date Filters
    // ---------------------------------------------------------
    const [searchText, setSearchText] = useState(
        pagination?.keywords || ""
    );

    const [fromDate, setFromDate] = useState(
        pagination?.from_date || ""
    );

    const [toDate, setToDate] = useState(
        pagination?.to_date || ""
    );

    // ---------------------------------------------------------
    // Wallet Transaction API
    // ---------------------------------------------------------
    const {
        data: walletData,
        isLoading,
        isFetching,
        error,
    } = useGetWalletTransactionListQuery({
        ...pagination,
        keywords: pagination?.keywords || "",
        from_date: pagination?.from_date || "",
        to_date: pagination?.to_date || "",
    });

    // ---------------------------------------------------------
    // Apply Search and Filters
    // ---------------------------------------------------------
    const handleApplyFilter = () => {
        handlePaginationState({
            ...pagination,
            pageNo: 0,
            keywords: searchText.trim(),
            from_date: fromDate,
            to_date: toDate,
        });
    };

    // ---------------------------------------------------------
    // Clear Search and Filters
    // ---------------------------------------------------------
    const handleClearFilter = () => {
        setSearchText("");
        setFromDate("");
        setToDate("");

        handlePaginationState({
            ...pagination,
            pageNo: 0,
            keywords: "",
            from_date: "",
            to_date: "",
        });
    };

    // ---------------------------------------------------------
    // Format Transaction Source
    // ---------------------------------------------------------
    const formatSource = (source) => {
        switch (source) {
            case "ACTIVITY_TRANSFER":
                return "Activity Transfer";

            case "PAYMENT":
                return "Payment";

            case "MANUAL":
                return "Manual";

            default:
                return source || "-";
        }
    };

    // ---------------------------------------------------------
    // Table Columns
    // ---------------------------------------------------------
    const columns = [
        {
            title: "Sr. No.",
            sort: false,
            minWidth: 80,
        },
        {
            title: "Member ID",
            field: "member_id",
            sort: true,
            minWidth: 110,
        },
        {
            title: "Member Name",
            field: "member_name",
            sort: true,
            minWidth: 170,
        },
        {
            title: "Type",
            field: "type",
            sort: true,
            minWidth: 100,
        },
        {
            title: "Source",
            field: "source",
            sort: true,
            minWidth: 150,
        },
        {
            title: "Amount",
            field: "amount",
            sort: true,
            minWidth: 130,
        },
        {
            title: "Balance After",
            field: "balance_after",
            sort: true,
            minWidth: 140,
        },
        {
            title: "Activity / Purpose",
            field: "activity_name",
            sort: true,
            minWidth: 200,
        },
        {
            title: "Description",
            field: "description",
            sort: false,
            minWidth: 240,
        },
        {
            title: "Reference ID",
            field: "reference_id",
            sort: false,
            minWidth: 200,
        },
        {
            title: "Transaction Date",
            field: "createdAt",
            sort: true,
            minWidth: 170,
        },
    ];

    // ---------------------------------------------------------
    // API Data
    // ---------------------------------------------------------
    const walletRecords = walletData?.result || [];
    const totalCount = walletData?.count || 0;

    // ---------------------------------------------------------
    // Table Rows
    // ---------------------------------------------------------
    const tableData =
        !isLoading && walletRecords.length > 0
            ? walletRecords.map((row, index) => (
                  <TableRow key={row._id}>
                      {/* Sr. No. */}
                      <TableCell align="center">
                          {index +
                              1 +
                              Number(pagination?.pageNo || 0) *
                                  Number(pagination?.limit || 10)}
                      </TableCell>

                      {/* Member ID */}
                      <TableCell align="center">
                          {row?.member_id || "-"}
                      </TableCell>

                      {/* Member Name */}
                      <TableCell align="left">
                          {row?.member_name || "-"}
                      </TableCell>

                      {/* Transaction Type */}
                      <TableCell align="center">
                          <Typography
                              variant="body2"
                              sx={{
                                  fontWeight: 600,
                                  color:
                                      row?.type === "CREDIT"
                                          ? "success.main"
                                          : row?.type === "DEBIT"
                                          ? "error.main"
                                          : "text.primary",
                              }}
                          >
                              {row?.type || "-"}
                          </Typography>
                      </TableCell>

                      {/* Transaction Source */}
                      <TableCell align="center">
                          {formatSource(row?.source)}
                      </TableCell>

                      {/* Transaction Amount */}
                      <TableCell align="right">
                          <Typography
                              variant="body2"
                              sx={{
                                  fontWeight: 600,
                                  color:
                                      row?.type === "CREDIT"
                                          ? "success.main"
                                          : row?.type === "DEBIT"
                                          ? "error.main"
                                          : "text.primary",
                                  whiteSpace: "nowrap",
                              }}
                          >
                              {row?.type === "CREDIT" ? "+" : "−"}₹
                              {Number(row?.amount || 0).toFixed(2)}
                          </Typography>
                      </TableCell>

                      {/* Balance After Transaction */}
                      <TableCell align="right">
                          ₹{Number(row?.balance_after || 0).toFixed(2)}
                      </TableCell>

                      {/* Activity / Purpose */}
                      <TableCell align="left">
                          {row?.activity_name || "-"}
                      </TableCell>

                      {/* Description */}
                      <TableCell align="left">
                          {row?.description || "-"}
                      </TableCell>

                      {/* Reference ID */}
                      <TableCell align="center">
                          {row?.reference_id || "-"}
                      </TableCell>

                      {/* Transaction Date */}
                      <TableCell align="center">
                          {row?.createdAt
                              ? handleDateTimeDefault(row.createdAt)
                              : "-"}
                      </TableCell>
                  </TableRow>
              ))
            : (
                  <TableRow
                      sx={{
                          "td, th": {
                              border: 0,
                              padding: "10px",
                          },
                      }}
                  >
                      <TableCell
                          colSpan={columns.length}
                          align="center"
                      >
                          {error
                              ? "Failed to load wallet transactions"
                              : "Data not found"}
                      </TableCell>
                  </TableRow>
              );

    // ---------------------------------------------------------
    // Render
    // ---------------------------------------------------------
    return (
        <Paper sx={{ p: 2 }}>
            <Typography
                variant="h5"
                sx={{
                    mb: 2,
                    fontWeight: 600,
                }}
            >
                Wallet History
            </Typography>

            {/* Search and Date Filters */}
            <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{ mb: 2 }}
            >
                {/* Search */}
                <Grid item xs={12} md={5}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Search Wallet History"
                        placeholder="Member ID, Name, Activity, Description..."
                        value={searchText}
                        onChange={(e) =>
                            setSearchText(e.target.value)
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleApplyFilter();
                            }
                        }}
                    />
                </Grid>

                {/* From Date */}
                <Grid item xs={12} sm={6} md={2}>
                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="From Date"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={fromDate}
                        onChange={(e) =>
                            setFromDate(e.target.value)
                        }
                    />
                </Grid>

                {/* To Date */}
                <Grid item xs={12} sm={6} md={2}>
                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="To Date"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={toDate}
                        onChange={(e) =>
                            setToDate(e.target.value)
                        }
                    />
                </Grid>

                {/* Search Button */}
                <Grid item xs={12} sm={6} md={1.5}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleApplyFilter}
                    >
                        Search
                    </Button>
                </Grid>

                {/* Clear Button */}
                <Grid item xs={12} sm={6} md={1.5}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleClearFilter}
                    >
                        Clear
                    </Button>
                </Grid>
            </Grid>

            {/* Wallet History Table */}
            <TableCommon
                columns={columns}
                tableData={tableData}
                count={totalCount}
                loading={isLoading || isFetching}
                pagination={pagination}
                handlePagination={handlePaginationState}
            />
        </Paper>
    );
};

export default WalletTransactionsContainer;